import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";

const CARD_TYPES = [
  {
    type: "image_text",
    name: "Image + Text",
    description:
      "A photo or graphic with a headline and short body. The fastest way to make a point.",
    Icon: ImageIcon,
    accent: "#6d28d9",
  },
  {
    type: "video",
    name: "Video",
    description:
      "A full-bleed autoplay video, muted by default. Best for demos, walkthroughs, and quick how-tos.",
    Icon: OndemandVideoIcon,
    accent: "#2563eb",
  },
  {
    type: "poll",
    name: "Poll",
    description:
      "Ask a question with no right answer. Drives engagement and surfaces team opinions.",
    Icon: PollOutlinedIcon,
    accent: "#f59e0b",
  },
  {
    type: "quiz",
    name: "Trivia",
    description:
      "A single multiple-choice question with instant correct/incorrect feedback. Mark one or more choices correct.",
    Icon: QuestionAnswerOutlinedIcon,
    accent: "#16a34a",
  },
];

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 720,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 4,
  boxShadow: 24,
  p: 4,
  overflow: "auto",
};

/**
 * Modal shown from the editor's "+ Add Card" button. Four large tiles, one
 * per card type. Clicking a tile commits the type and the parent appends
 * a default card of that type to the deck.
 */
export function SwipeCardTypePickerModal({ open, onClose, onSelect }) {
  return (
    <Modal open={open} onClose={onClose} aria-labelledby="card-type-picker">
      <Box sx={modalStyle}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2
              id="card-type-picker"
              className="text-2xl font-bold text-slate-900"
            >
              Add a card
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Pick a type — you can change content later.
            </p>
          </div>
          <IconButton onClick={onClose} aria-label="Close" sx={{ borderRadius: "8px" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-1 gap-4">
          {CARD_TYPES.map(({ type, name, description, Icon, accent }) => (
            <button
              key={type}
              type="button"
              onClick={() => onSelect(type)}
              className="group text-left rounded-2xl border-2 border-slate-200 hover:border-violet-500 hover:shadow-lg transition-all duration-200 p-5 bg-white"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 text-white"
                style={{ backgroundColor: accent }}
              >
                <Icon style={{ fontSize: 26 }} />
              </div>
              <div className="text-base font-semibold text-slate-900 mb-1 group-hover:text-violet-700 transition-colors">
                {name}
              </div>
              <div className="text-sm text-slate-500 leading-relaxed">
                {description}
              </div>
            </button>
          ))}
        </div>
      </Box>
    </Modal>
  );
}
